import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {DocumentOption} from '../../documents/document-option';
import {Entity} from '../../entity/entity';
import {DocumentService} from '../../services/document-service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {LineSelectionDialogComponent} from '../line-selection-dialog/line-selection-dialog.component';
import {EntityLine} from '../../entity/entity-line';
import {DocumentGenerationInput} from '../../documents/document-generation-input';
import {MatTooltip} from '@angular/material/tooltip';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {BrandID} from "../../enums/brand-id";
import {ProjectProductType} from "../../enums/project-product-type";
import {LeadTimeUtils} from "../../utils/lead-time-utils";
import {StringUtils} from '../../utils/string-utils';

@Component({
    selector: 'document-generation-dialog',
    templateUrl: './document-generation-dialog.component.html',
    styleUrls: ['./document-generation-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatRadioGroup, FormsModule, MatRadioButton, MatButton, MatTooltip, MatDialogActions]
})
export class DocumentGenerationDialogComponent {
  private readonly documentService = inject(DocumentService);
  private readonly dialogRef = inject<MatDialogRef<DocumentGenerationDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA);


  private timeoutMinutes = 2;
  private timeoutValue = this.timeoutMinutes * 60 * 1000;
  private timeoutMessage = 'Generating the selected documents is taking longer than expected.'
    + '\nPlease come back in a few minutes to view the documents';
  private readonly allEntityLineIds: string[];
  entity: Entity;
  documentOptions: DocumentOption[];
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  selectedDocuments: string[];
  lineSelection = 'all';
  readonly selectedLines = signal<string>(undefined!);

  constructor
  () {
    this.entity = this.data.entity;
    this.documentOptions = this.createDocumentOptionList();
    this.selectedDocuments = this.getSelectedDefaultDocuments();
    this.allEntityLineIds = this.data.allLines;
  }

  private getSelectedDefaultDocuments(): string[] {
    switch (this.entity.brandID) {
      case BrandID.CLM:
        return this.getClmDefaultDocuments();
      case BrandID.IEC:
        return this.getIecDefaultDocuments();
      default:
        this.errorMessage.set('Unknown brand: ' + this.entity.brandID);
    }
    return undefined!;
  }

  private getClmDefaultDocuments(): string[] {
    return ['TRANSMITTAL'];
  }

  private getIecDefaultDocuments(): string[] {
    if (this.isSchedule()) {
      return [];
    } else {
      return ['TRANSMITTAL'];
    }
  }

  isSchedule(): boolean {
    return 'SC' === (this.entity?.releaseTypeId || '');
  }

  private createDocumentOptionList(): DocumentOption[] {
    switch (this.entity.brandID) {
      case BrandID.CLM:
        return this.createClmDocumentOptionList();
      case BrandID.IEC:
        return this.createIecDocumentOptionList();
      default:
        this.errorMessage.set('Unknown brand: ' + this.entity.brandID);
    }
    return undefined!;
  }

  private createClmDocumentOptionList(): DocumentOption[] {
    const options: DocumentOption[] = [];
    options.push(new DocumentOption('TRANSMITTAL', 'Transmittal', 'EXCEL', ['EXCEL', 'PDF']));
    if (this.entity.projectProductType != ProjectProductType.RES && !LeadTimeUtils.isStockProgram(this.entity.leadTimeCode)) {
      options.unshift(new DocumentOption('RATING_SCHEDULE', 'Performance Schedule', 'EXCEL', ['EXCEL']));
      options.push(new DocumentOption('PRODUCT_SUBMITTAL_COVER_SHEET', 'Product Submittal Cover Page', 'PDF', ['PDF', 'WORD']));
      options.push(new DocumentOption('PERFORMANCE_CHARACTERISTICS_DATASHEET', 'Performance Characteristics Datasheet', 'PDF', ['PDF']));
      options.push(new DocumentOption('UNIT_FEATURES', 'Unit Features', 'PDF', ['PDF']));
      options.push(new DocumentOption('DIMENSIONAL_DRAWING', 'Dimensional Drawing', 'PDF', ['PDF']));
      options.push(new DocumentOption('GENERAL_DATA', 'General Data', 'PDF', ['PDF']));
      options.push(new DocumentOption('PERFORMANCE_DATA', 'Performance Data', 'PDF', ['PDF']));
      options.push(new DocumentOption('ENGINEERING_SPECIFICATIONS', 'Engineering Specifications', 'PDF', ['PDF']));
    }
    return options;
  }

  private createIecDocumentOptionList(): DocumentOption[] {
    const options: DocumentOption[] = [];
    options.push(new DocumentOption('RATING_SCHEDULE', 'Rating Schedule', 'CSV', ['CSV']));
    if (!this.isSchedule()) {
      options.push(new DocumentOption('TRANSMITTAL', 'Transmittal', 'EXCEL', ['EXCEL', 'WORD']));
    }
    options.push(new DocumentOption('DIMENSIONAL_DRAWING', 'Dimensional Drawing', 'PDF', ['PDF']));
    options.push(new DocumentOption('MECHANICAL_SPECS', 'Mechanical Specs', 'WORD', ['WORD', 'PDF']));
    options.push(new DocumentOption('RATINGS_DATA', 'Ratings Data', 'PDF', ['PDF']));
    options.push(new DocumentOption('PRODUCT_SUBMITTAL_COVER_SHEET', 'Product Submittal Cover Sheet', 'WORD', ['WORD']));
    options.push(new DocumentOption('UNIT_INFORMATION', 'Unit Information', 'EXCEL', ['EXCEL']));
    options.push(new DocumentOption('SHIPPING_SPECIFICATION', 'Shipping Specification', 'PDF', ['PDF']));
    if (!this.isSchedule()) {
      options.push(new DocumentOption('VALVE_PACKAGE_SUBMITTAL', 'Valve Package Submittal', 'PDF', ['PDF']));
      options.push(new DocumentOption('ELECTRICAL_SUBMITTAL', 'Electrical Submittal', 'PDF', ['PDF']));
      options.push(new DocumentOption('COMPONENT_CUT_SHEET', 'Component Cut-Sheet', 'PDF', ['PDF']));
    }
    return options;
  }

  saveAndClose(): void {
    const selectedOptions: DocumentOption[] = this.documentOptions.filter(option => this.isSelected(option));
    if (selectedOptions.length === 0) {
      this.errorMessage.set('No documents were selected to generate');
      this.isBusy.set(false);
    } else {
      let lineIds: string[] = [];
      if (this.lineSelection === 'all') {
        lineIds = this.allEntityLineIds;
      } else {
        if ((this.selectedLines() || '').length === 0) {
          this.errorMessage.set('No lines were selected for the documents');
          this.isBusy.set(false);
        } else {
          lineIds = this.selectedLines().split(',');
        }
      }
      if (lineIds.length > 0) {
        this.isBusy.set(true);
        selectedOptions.forEach(option => option.lineIds = lineIds);
        const input: DocumentGenerationInput = new DocumentGenerationInput();
        input.options = selectedOptions;
        input.entityId = this.entity.id.entityId;
        input.revision = this.entity.id.entityRevision;
        const subscription = this.documentService.generate(input).subscribe({
          next: () => this.dialogRef.close(null),
          error: (error: HttpErrorResponse) => this.handleError(error)
        });
        setTimeout(() => {
          subscription.unsubscribe();
          this.dialogRef.close(this.timeoutMessage);
        }, this.timeoutValue);
      }
    }
  }

  generateSubmittalPackage(): void {
    this.isBusy.set(true);
    const selectedOptions: DocumentOption[] = this.documentOptions.filter(option => this.isSelected(option));
    if (selectedOptions.length === 0) {
      this.errorMessage.set('No documents were selected to generate');
      this.isBusy.set(false);
    } else {
      let lineIds: string[] = [];
      if (this.lineSelection === 'all') {
        lineIds = this.allEntityLineIds;
      } else {
        if ((this.selectedLines() || '').length === 0) {
          this.errorMessage.set('No lines were selected for the documents');
          this.isBusy.set(false);
        } else {
          lineIds = this.selectedLines().split(',');
        }
      }
      if (lineIds.length > 0) {
        selectedOptions.forEach(option => option.lineIds = lineIds);
        const input: DocumentGenerationInput = new DocumentGenerationInput();
        input.options = selectedOptions;
        input.entityId = this.entity.id.entityId;
        input.revision = this.entity.id.entityRevision;
        const subscription = this.documentService.generateProjectSubmittal(input).subscribe({
          next: () => this.dialogRef.close(null),
          error: (error: HttpErrorResponse) => this.handleError(error)
        });
        setTimeout(() => {
          subscription.unsubscribe();
          this.dialogRef.close(this.timeoutMessage);
        }, this.timeoutValue);
      }
    }
  }

  updateSelection(documentOption: DocumentOption): void {
    if (this.isSelected(documentOption)) {
      this.selectedDocuments.splice(this.selectedDocuments.indexOf(documentOption.documentType, 0), 1);
    } else {
      this.selectedDocuments.push(documentOption.documentType);
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    if (typeof error?.error === 'string' && StringUtils.isNotBlank(error.error)) {
      this.errorMessage.set(StringUtils.getErrorMessage(error));
    } else {
      this.errorMessage.set('There was an error generating the documents.');
    }
  }

  toggleAllSelections(): void {
    if (this.allItemsSelected()) {
      this.selectedDocuments = [];
    } else {
      this.selectedDocuments = this.documentOptions.map(docOption => docOption.documentType);
    }
  }

  allItemsSelected(): boolean {
    return this.selectedDocuments.length === this.documentOptions.length;
  }

  isSelected(documentOption: DocumentOption) {
    return this.selectedDocuments.indexOf(documentOption.documentType, 0) > -1;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  selectLines(): void {
    this.errorMessage.set(undefined!);
    const selectionDialog = this.dialog.open(LineSelectionDialogComponent, {
      width: '1064px',
      height: '768px',
      data: {entityKey: this.entity.id, selectedLines: this.selectedLines()},
    });
    selectionDialog.afterClosed().subscribe((result: EntityLine[]) => {
      if (result && result.length > 0) {
        this.selectedLines.set(result.map(line => line.lineId)
          .sort((first, second) => {
            const firstAsNumber: number = +first;
            const secondAsNumber: number = +second;
            if (isNaN(firstAsNumber) || isNaN(secondAsNumber)) {
              return first.localeCompare(second);
            } else {
              return firstAsNumber - secondAsNumber;
            }
          }).join(','));
      }
    });
  }

  clearLineSelection(): void {
    this.selectedLines.set(undefined!);
  }

  getTooltip(documentOption: DocumentOption): string {
    switch (this.entity.brandID) {
      case BrandID.CLM:
      case BrandID.IEC:
        if (this.isExcludedFromSubmittalPackage(documentOption.documentType)) {
          return 'This document cannot be included in the submittal package. If checked, a separate document will be generated.';
        }
        return '';
      default:
        this.errorMessage.set('Unknown Brand: ' + this.entity.brandID);
    }
    return undefined!;
  }

  private isExcludedFromSubmittalPackage(documentType: string): boolean {
    const excludedDocs: string[] = this.getSubmittalExcludedDocuments();
    return excludedDocs.indexOf(documentType) >= 0;
  }

  private getSubmittalExcludedDocuments(): string[] {
    switch (this.entity.brandID) {
      case BrandID.CLM:
        return ['RATING_SCHEDULE', 'TRANSMITTAL'];
      case BrandID.IEC:
        return ['RATING_SCHEDULE', 'TRANSMITTAL', 'UNIT_INFORMATION'];
      default:
        this.errorMessage.set('Unknown Brand: ' + this.entity.brandID);
    }
    return undefined!;
  }

}

